/**
 * paperlab 翻页动画驱动
 *
 * 把 bookStore 的离散翻页状态机（startFlip/endFlip）升级为连续进度驱动：
 * - 调用 flipToNext/flipToPrev 时用 gsap 补间 flipProgress 0→1
 * - 完成时 setCurrentPage 并复位状态机
 * - PaperBook 组件在 useFrame 里把 flipProgress 写入 paperlab handle
 */
import { useCallback, useRef } from 'react'
import gsap from 'gsap'
import { useBookStore } from '../stores/bookStore'
import { FLIP_DURATION_MS, FLIP_EASE } from '../lib/paperlab/paperConfig'

interface UsePaperFlipResult {
  flipToNext: () => void
  flipToPrev: () => void
  /** 0..1 的连续翻页进度（直接喂给 paperlab behavior.progress） */
  progress: number
  isFlipping: boolean
  direction: 'next' | 'prev' | null
  canFlipNext: boolean
  canFlipPrev: boolean
}

/**
 * 包装 bookStore 的翻页状态，用 gsap 产生连续 progress。
 *
 * 设计取舍：paperlab 的 progress 是声明式参数，但每帧改 props 会触发 React
 * 重渲染。因此这里用 ref 持有最新 progress，由 PaperBook 在 useFrame 中
 * 通过 PaperHandle.set('progress', v) 直写，避免重渲染。
 */
export function usePaperFlip(): UsePaperFlipResult {
  const { currentPage, totalPages, isFlipping, direction, flipProgress, startFlip, endFlip, setCurrentPage } =
    useBookStore()
  const tweenRef = useRef<gsap.core.Tween | null>(null)

  const flipToNext = useCallback(() => {
    if (isFlipping) return
    if (currentPage >= totalPages - 1) return
    startFlip('next')
    const tween = gsap.to(
      {},
      {
        duration: FLIP_DURATION_MS / 1000,
        ease: FLIP_EASE,
        onUpdate: () => {
          useBookStore.setState({ flipProgress: tween.progress() })
        },
        onComplete: () => {
          endFlip()
          setCurrentPage(currentPage + 1)
          useBookStore.setState({ flipProgress: 0 })
        },
      },
    )
    tweenRef.current = tween
  }, [currentPage, totalPages, isFlipping, startFlip, endFlip, setCurrentPage])

  const flipToPrev = useCallback(() => {
    if (isFlipping) return
    if (currentPage <= 0) return
    // 上一页：纸张从左侧翻回右侧，视觉上 progress 从 1 → 0
    useBookStore.setState({ flipProgress: 1 })
    startFlip('prev')
    const tween = gsap.to(
      {},
      {
        duration: FLIP_DURATION_MS / 1000,
        ease: FLIP_EASE,
        onUpdate: () => {
          useBookStore.setState({ flipProgress: 1 - tween.progress() })
        },
        onComplete: () => {
          endFlip()
          setCurrentPage(currentPage - 1)
          useBookStore.setState({ flipProgress: 0 })
        },
      },
    )
    tweenRef.current = tween
  }, [currentPage, isFlipping, startFlip, endFlip, setCurrentPage])

  return {
    flipToNext,
    flipToPrev,
    progress: flipProgress,
    isFlipping,
    direction,
    canFlipNext: currentPage < totalPages - 1,
    canFlipPrev: currentPage > 0,
  }
}